import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

import { BACKEND_URL } from '../lib/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  // Listen for the global axios interceptor's "refresh failed" signal so the
  // whole app can react in one place: clear user state, toast the user, and
  // bounce them to /login while preserving their current path via ?next=.
  useEffect(() => {
    const handler = () => {
      setUser(false);
      try { window.localStorage.removeItem('vinepass:was-authed'); } catch { /* storage blocked */ }
      try {
        toast.error('Session expired. Please log in again.');
      } catch { /* toast may not be ready during SSR */ }
      const path = window.location.pathname + window.location.search;
      // Don't loop if we're already on an auth page.
      if (/^\/(login|register|forgot-password|reset-password)/.test(window.location.pathname)) {
        return;
      }
      const next = encodeURIComponent(path || '/');
      window.location.assign(`/login?session=expired&next=${next}`);
    };
    window.addEventListener('auth:session-expired', handler);
    return () => window.removeEventListener('auth:session-expired', handler);
  }, []);

  // Tiny localStorage flag used by the global axios interceptor to decide
  // whether a refresh-token failure means "session expired" (user *was* signed
  // in and should be told) vs "never signed in" (anonymous boot — silent).
  const setAuthedFlag = () => {
    try { window.localStorage.setItem('vinepass:was-authed', '1'); } catch { /* storage blocked */ }
  };
  const clearAuthedFlag = () => {
    try { window.localStorage.removeItem('vinepass:was-authed'); } catch { /* storage blocked */ }
  };

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true,
      });
      setUser(response.data);
      setAuthedFlag();
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(response.data);
      setAuthedFlag();
      return { success: true };
    } catch (error) {
      // Surface the best available message — backend detail first, then
      // network-level issues, then a generic fallback.
      let errorMessage;
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
      } else if (error.response?.status) {
        errorMessage = `Login failed (HTTP ${error.response.status})`;
      } else if (error.message) {
        errorMessage = `Login failed: ${error.message}`;
      } else {
        errorMessage = 'Login failed — could not reach the server. Please check your connection.';
      }
      console.error('[Auth] login error', error);
      return { success: false, error: errorMessage };
    }
  };

  const register = async (name, email, password, role = 'customer') => {
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/register`,
        { name, email, password, role },
        { withCredentials: true }
      );
      setUser(response.data);
      setAuthedFlag();
      return { success: true };
    } catch (error) {
      let errorMessage;
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
      } else if (error.response?.status) {
        errorMessage = `Registration failed (HTTP ${error.response.status})`;
      } else if (error.message) {
        errorMessage = `Registration failed: ${error.message}`;
      } else {
        errorMessage = 'Registration failed — could not reach the server. Please check your connection.';
      }
      console.error('[Auth] register error', error);
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      setUser(false);
      clearAuthedFlag();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;